import React, { useState, useRef } from "react";
import { Button, Group, Transition, Text, Anchor } from "@mantine/core";
import { TextInput, PasswordInput } from "react-hook-form-mantine";

import {
  useLogin,
  usePasswordlessLogin,
  useSendResetPasswordEmail,
} from "../../api/auth";
import { useForm } from "react-hook-form";
import {
  web_passwordlessSchema,
  web_regularLoginSchema,
} from "@congrs/schemas";
import resolver from "@congrs/resolver";
import { queryClient } from "../../services/react-query";

function LoginForm() {
  const [mode, setMode] = useState("email"); // 'email', 'password', 'forgot'
  const passwordInputRef = useRef(null);

  const handleEnterPassword = () => {
    setMode("password");
    // I have to fix the focus issue, because
    // react-hook-form-mantine component doesn't forward the refs
    setTimeout(() => {
      passwordInputRef.current?.focus();
    }, 300);
  };

  const handleForgotPassword = () => {
    setMode("forgot");
  };

  const handleBackToLogin = () => {
    setMode("email");
  };

  const schema =
    mode === "password" ? web_regularLoginSchema : web_passwordlessSchema;

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { email: "", password: "" },
    resolver: resolver(schema),
    mode: "onBlur",
  });

  const { mutate, isPending, isSuccess, isError, error } = useLogin();
  const {
    mutate: sendPasswordlessLogin,
    isPending: sendingEmail,
    isSuccess: emailSent,
  } = usePasswordlessLogin();
  const {
    mutate: sendResetPasswordEmail,
    isPending: sendingResetPasswordEmail,
    isSuccess: resetPasswordEmailSent,
    isError: isResetPasswordEmailError,
    error: resetPasswordEmailError,
  } = useSendResetPasswordEmail();

  const handleLogin = (data) => {
    if (mode === "email" && !data.password) {
      sendPasswordlessLogin(
        {
          email: data.email,
        },
        {
          onSuccess: () => {
            reset({ email: "" });
          },
        }
      );
      return;
    }

    if (mode === "forgot") {
      sendResetPasswordEmail(data, {
        onSuccess: () => {
          reset({ email: "" });
        },
      });
      return;
    }

    mutate(
      {
        email: data.email,
        password: data.password,
      },
      {
        onSuccess: (data) => {
          localStorage.setItem("token", data.token);
          queryClient.invalidateQueries();
        },
        onError: (error) => {
          console.error("Login failed:", error);
          reset({ email: data.email, password: "" });
          passwordInputRef.current?.focus();
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(handleLogin)}>
      {/* Email mode - initial state */}
      {mode === "email" && (
        <>
          <TextInput
            label="Email address"
            placeholder="hello@gmail.com"
            size="md"
            control={control}
            name="email"
            withAsterisk
          />
          <Group mt="md" grow>
            <Button
              size="sm"
              h="40px"
              type="submit"
              loading={sendingEmail}
              color="#2b3846"
            >
              Passwordless Login
            </Button>
            <Button
              size="sm"
              onClick={handleEnterPassword}
              color="#2b3846"
              h="40px"
            >
              Enter Password
            </Button>
          </Group>
          {emailSent && (
            <Text c="green" mt="md" fw="500">
              Passwordless login email sent! Please check your inbox.
            </Text>
          )}
        </>
      )}

      {/* Single transition for both password and forgot password modes */}
      <Transition
        mounted={mode === "password" || mode === "forgot"}
        transition="fade"
        duration={300}
        timingFunction="ease"
      >
        {(styles) => (
          <div style={styles}>
            {mode === "password" && (
              <>
                <TextInput
                  label="Email address"
                  placeholder="hello@gmail.com"
                  size="md"
                  control={control}
                  name="email"
                  withAsterisk
                />

                <PasswordInput
                  label="Password"
                  placeholder="Your password"
                  mt="md"
                  size="md"
                  ref={passwordInputRef}
                  withAsterisk
                  control={control}
                  name="password"
                />

                <Anchor
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    handleForgotPassword();
                  }}
                  c="#2b3846"
                  size="sm"
                  mt="xs"
                  py="xs"
                  style={{ display: "block", textAlign: "center" }}
                >
                  Forgot password?
                </Anchor>

                <Button
                  fullWidth
                  mt="sm"
                  size="sm"
                  type="submit"
                  loading={isPending}
                  color="#2b3846"
                  h="40px"
                  disabled={isSuccess}
                >
                  Login
                </Button>

                {isSuccess && (
                  <Text c="green" mt="md">
                    Login successful!
                  </Text>
                )}

                {isError && (
                  <Text c="red" mt="md">
                    {error?.message || "Login failed"}
                  </Text>
                )}
              </>
            )}

            {mode === "forgot" && (
              <>
                <Text size="lg" fw="600">
                  Reset Password
                </Text>
                <Text size="sm" c="dimmed" mb="md">
                  Enter your email address and we'll send you a link to reset
                  your password.
                </Text>

                <TextInput
                  label="Email address"
                  placeholder="hello@gmail.com"
                  size="md"
                  control={control}
                  name="email"
                  withAsterisk
                />

                {resetPasswordEmailSent && (
                  <Text c="green" mt="md" fw="500">
                    Reset password email sent! Please check your inbox.
                  </Text>
                )}

                {isResetPasswordEmailError && (
                  <Text c="red" mt="md" fw="500">
                    {resetPasswordEmailError?.message ||
                      "Failed to send reset password email"}
                  </Text>
                )}

                <Button
                  fullWidth
                  mt="md"
                  size="sm"
                  type="submit"
                  color="#2b3846"
                  h="40px"
                  loading={sendingResetPasswordEmail}
                >
                  Send Reset Link
                </Button>

                <Anchor
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    handleBackToLogin();
                  }}
                  c="#2b3846"
                  size="sm"
                  mt="md"
                  style={{ display: "block", textAlign: "center" }}
                >
                  Back to Login
                </Anchor>
              </>
            )}
          </div>
        )}
      </Transition>
    </form>
  );
}

export default LoginForm;
